<template>
  <div class="page-manage">
    <h1 class="title">Admin / Manage</h1>

    <!-- Global notifications -->
    <article v-if="error" class="message is-danger">
      <div class="message-body">
        {{ error }}
      </div>
    </article>

    <article v-if="notice" class="message is-info">
      <div class="message-body">
        {{ notice }}
      </div>
    </article>

    <!-- Organization overview -->
    <div class="box" v-if="organization">
      <h2 class="subtitle">
        Organization overview
        <p>{{ organization.name }}</p>
      </h2>
    </div>

    <!-- Members table -->
    <div class="box">
      <div class="level">
        <div class="level-left">
          <h2 class="subtitle">Members</h2>
        </div>
        <div class="level-right">
          <button
            class="button is-small is-light"
            @click="fetchOverview"
            :disabled="loading"
          >
            <span class="icon"><i class="fas fa-sync"></i></span>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div v-if="loading" class="has-text-grey">Loading...</div>

      <table v-else class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Invited</th>
            <th class="has-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!members.length">
            <td colspan="4" class="has-text-grey has-text-centered">
              No members found.
            </td>
          </tr>
          <tr v-for="m in members" :key="m.id">
            <td>
              <strong>{{ m.display_name || '—' }}</strong>
            </td>
            <td style="max-width: 220px">
              <!-- role select -->
              <div class="select is-small is-fullwidth">
                <select
                  :value="m.role"
                  @change="onChangeRole(m, $event.target.value)"
                >
                  <option
                    v-for="r in roleChoices"
                    :key="r.value"
                    :value="r.value"
                  >
                    {{ r.display }}
                  </option>
                </select>
              </div>
            </td>
            <td>{{ formatDate(m.invited_at) }}</td>
            <td class="has-text-right">
              <button
                class="button is-danger is-light is-small"
                @click="deleteMember(m)"
                :disabled="busyIds.has(m.id)"
              >
                <span class="icon"><i class="fas fa-trash"></i></span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Add member -->
      <h3 class="subtitle is-6">Invite / add a member</h3>
      <div class="columns is-mobile">
        <div class="column is-6">
          <input
            class="input"
            type="email"
            placeholder="Email"
            v-model.trim="newMemberEmail"
          />
        </div>
        <div class="column is-3">
          <div class="select is-fullwidth">
            <select v-model="newMemberRole">
              <option v-for="r in roleChoices" :key="r.value" :value="r.value">
                {{ r.display }}
              </option>
            </select>
          </div>
        </div>
        <div class="column is-3 has-text-right">
          <button
            class="button is-success"
            @click="createMember"
            :disabled="creating || !newMemberEmail"
          >
            <span v-if="creating" class="icon"
              ><i class="fas fa-spinner fa-spin"></i
            ></span>
            <span>{{ creating ? 'Adding…' : 'Add member' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// NOTE: comments in English as requested
import api from '@/utils/axios';

export default {
  name: 'ManageView',

  data() {
    return {
      loading: false,
      creating: false,
      error: '',
      notice: '',
      organization: null, // { id, name }
      members: [], // array of memberships with embedded user
      roleChoices: [], // [{value, display}]
      newMemberEmail: '',
      newMemberRole: 'member',
      busyIds: new Set(), // track per-row busy state
    };
  },

  async mounted() {
    await this.fetchOverview();
  },

  methods: {
    async fetchOverview() {
      this.loading = true;
      this.error = '';
      this.notice = '';
      try {
        // Server returns: { organization:{id,name}, members:[...], role_choices:[...] }
        const { data } = await api.get('organization/manage/overview/');
        this.organization = data.organization || null;
        this.members = Array.isArray(data.members) ? data.members : [];
        this.roleChoices = Array.isArray(data.role_choices)
          ? data.role_choices
          : [];

        // Pick sensible default role for the "add member" form
        const memberChoice = this.roleChoices.find(
          (r) => String(r.value).toLowerCase() === 'member'
        );
        this.newMemberRole = memberChoice
          ? memberChoice.value
          : this.roleChoices[0]?.value || 'member';
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e.message;
        if (status === 400 && /No active organization/i.test(detail)) {
          this.error =
            'Aucune organisation active. Ouvre “My Account” > choisis/édite ton organisation, puis reviens.';
        } else if (status === 404) {
          this.error =
            'Endpoint introuvable. Vérifie l’inclusion des URLs: /api/v1/organization/admin/overview/.';
        } else if (status === 401) {
          this.error = 'Session expirée. Merci de vous reconnecter.';
          this.$router.replace('/log-in');
        } else {
          this.error = `Overview error: ${detail}`;
        }
        console.warn('overview failed:', status, e?.response?.data);
      } finally {
        this.loading = false;
      }
    },

    async createMember() {
      this.creating = true;
      this.error = '';
      try {
        // Backend payload contract:
        // If your MembershipAdminSerializer expects "email" + "role", keep this.
        // If it expects "user" (id) + "role", adjust accordingly.
        const payload = {
          email: this.newMemberEmail,
          role: this.newMemberRole,
        };
        await api.post('organization/manage/members/', payload);
        this.newMemberEmail = '';
        await this.fetchOverview();
        this.notice = 'Member added successfully.';
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        this.error = `Add failed: ${detail}`;
        console.warn('create failed:', e?.response?.status, e?.response?.data);
      } finally {
        this.creating = false;
      }
    },

    async onChangeRole(m, newRole) {
      if (!newRole || newRole === m.role) return;
      this.busyIds.add(m.id);
      this.error = '';
      this.notice = '';
      try {
        await api.patch(`organization/manage/members/${m.id}/`, {
          role: newRole,
        });
        // update local state fast
        m.role = newRole;
        this.notice = 'Role updated.';
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        this.error = `Update failed: ${detail}`;
        console.warn('update failed:', e?.response?.status, e?.response?.data);
        // revert UI on failure
        this.$nextTick(() => this.fetchOverview());
      } finally {
        this.busyIds.delete(m.id);
      }
    },

    async deleteMember(m) {
      if (!confirm(`Remove ${m.display_name || 'this user'}?`)) return;
      this.busyIds.add(m.id);
      this.error = '';
      this.notice = '';
      try {
        await api.delete(`organization/manage/members/${m.id}/`);
        // remove locally
        this.members = this.members.filter((x) => x.id !== m.id);
        this.notice = 'Member removed.';
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        this.error = `Delete failed: ${detail}`;
        console.warn('delete failed:', e?.response?.status, e?.response?.data);
      } finally {
        this.busyIds.delete(m.id);
      }
    },

    formatDate(iso) {
      if (!iso) return '—';
      try {
        const d = new Date(iso);
        return d.toLocaleString();
      } catch {
        return iso;
      }
    },
  },
};
</script>

<style scoped>
.page-manage .level {
  margin-bottom: 0.75rem;
}
.page-manage .box + .box {
  margin-top: 1rem;
}
</style>
